import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { UNEVO_NAV } from "@/data-unevo";

const Logo = () => (
  <a href="#home" data-testid="unevo-logo" className="flex items-center gap-2.5 group">
    <span className="grid h-9 w-9 place-items-center rounded-xl border border-vibrant-orange/40 bg-vibrant-orange/10 font-heading text-lg font-black text-vibrant-orange transition-all group-hover:bg-vibrant-orange group-hover:text-white">
      U
    </span>
    <span className="font-heading text-xl font-extrabold tracking-tight text-white">
      Unevo<span className="text-vibrant-orange">.</span>
    </span>
  </a>
);

export default function UnevoHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="unevo-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-white/10 bg-[#0B0F1A]/85 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Logo />

        <nav className="hidden items-center gap-9 lg:flex">
          {UNEVO_NAV.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`unevo-nav-${l.label.toLowerCase()}`}
              className="relative text-sm font-medium text-white/70 transition-colors hover:text-white after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-vibrant-orange after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href="#contact"
            data-testid="unevo-cta-header"
            className="rounded-full bg-vibrant-orange px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-vibrant-orange/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-orange-600"
          >
            Get a Quote
          </a>
        </div>

        <button
          data-testid="unevo-mobile-menu-toggle"
          onClick={() => setMobileOpen((o) => !o)}
          className="grid h-10 w-10 place-items-center rounded-lg text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-[#0B0F1A]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {UNEVO_NAV.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-white/80 transition-colors hover:bg-white/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setMobileOpen(false)}
                className="mt-2 rounded-full bg-vibrant-orange px-6 py-3 text-center text-base font-semibold text-white"
              >
                Get a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
